import React from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { Button, Input, Label } from 'shared-portal-components-v2';

import PasswordRequirements from '@/components/molecules/PasswordRequirements/PasswordRequirements';
import SetPasswordError from '@/components/molecules/SetPasswordError/SetPasswordError';
import type { SetPasswordStage } from '@/types/AuthState';

export interface PasswordFormViewProps {
  password: string;
  confirmPassword: string;

  showPassword: boolean;
  showConfirmPassword: boolean;

  passwordError?: string;
  globalError?: string;
  stage: SetPasswordStage | undefined;

  isSubmitting: boolean;
  isContinueDisabled: boolean;

  onPasswordChange: (value: string) => void;
  onConfirmPasswordChange: (value: string) => void;

  onTogglePassword: () => void;
  onToggleConfirmPassword: () => void;

  onSubmit: () => void;
}

export function PasswordFormView(props: PasswordFormViewProps) {
  const {
    password,
    confirmPassword,
    showPassword,
    showConfirmPassword,
    passwordError,
    globalError,
    stage,
    isSubmitting,
    isContinueDisabled,
    onPasswordChange,
    onConfirmPasswordChange,
    onTogglePassword,
    onToggleConfirmPassword,
    onSubmit,
  } = props;

  return (
    <div className="w-full max-w-md flex flex-col gap-8">
      <div className="text-center flex flex-col gap-2">
        <h1 className="text-3xl font-bold text-foreground">Create Your Password</h1>
        <p className="text-muted-foreground text-lg">
          Choose a strong password to keep your account secure.
        </p>
      </div>

      <SetPasswordError stage={stage} message={globalError} mode="register" />

      <form
        className="flex flex-col gap-6"
        onSubmit={(event) => {
          event.preventDefault();
          onSubmit();
        }}
      >
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            <Label htmlFor="password" isRequired className="text-base">
              New Password
            </Label>

            <div className="relative">
              <Input
                id="password"
                type={showPassword ? 'text' : 'password'}
                required
                placeholder="Enter password"
                value={password}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  onPasswordChange(event.target.value);
                }}
                variant="outline"
                inputClassName="h-12 text-base sm:text-sm pr-12"
                error={passwordError}
              />

              <Button
                type="button"
                variant="tertiary"
                size="sm"
                className="absolute right-0 top-0 h-full px-3 py-2 text-muted-foreground hover:text-foreground bg-transparent shadow-none"
                onClick={onTogglePassword}
                disabled={isSubmitting}
              >
                {showPassword ? <EyeOff className="h-4 w-4" aria-hidden /> : <Eye className="h-4 w-4" aria-hidden />}
                <span className="sr-only">{showPassword ? 'Hide password' : 'Show password'}</span>
              </Button>
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <Label htmlFor="confirmPassword" isRequired className="text-base">
              Confirm Password
            </Label>

            <div className="relative">
              <Input
                id="confirmPassword"
                type={showConfirmPassword ? 'text' : 'password'}
                required
                placeholder="Re-enter password"
                value={confirmPassword}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  onConfirmPasswordChange(event.target.value);
                }}
                variant="outline"
                inputClassName="h-12 text-base sm:text-sm pr-12"
              />

              <Button
                type="button"
                variant="tertiary"
                size="sm"
                className="absolute right-0 top-0 h-full px-3 py-2 text-muted-foreground hover:text-foreground bg-transparent shadow-none"
                onClick={onToggleConfirmPassword}
                disabled={isSubmitting}
              >
                {showConfirmPassword ? <EyeOff className="h-4 w-4" aria-hidden /> : <Eye className="h-4 w-4" aria-hidden />}
                <span className="sr-only">{showConfirmPassword ? 'Hide password' : 'Show password'}</span>
              </Button>
            </div>

          </div>
        </div>

        <div className="bg-muted/50 rounded-lg p-4 mt-4">
          <PasswordRequirements
            password={password}
            confirmPassword={confirmPassword}
            includeMatchRequirement
          />
        </div>

        <div className="flex gap-3">
          <Button
            type="submit"
            variant="primary"
            size="md"
            className="flex-1 h-12 text-base font-semibold"
            isLoading={isSubmitting}
            loadingText="Submitting..."
            disabled={isContinueDisabled}
          >
            Continue
          </Button>
        </div>
      </form>
    </div>
  );
}

export default PasswordFormView;
